import { AnimatePresence, motion, useIsPresent } from 'framer-motion';
import type { CSSProperties, FunctionComponent, ReactNode } from 'react';

type MorphDirection = 'horizontal' | 'vertical';
type MorphAlign = 'center' | 'start';

type MorphProps = {
    readonly align?: MorphAlign;
    readonly children: ReactNode;
    readonly direction?: MorphDirection;
    readonly duration?: number;
    readonly stateKey: string;
};

type MotionState = {
    readonly opacity: number;
    readonly transform: string;
};

const easeOutQuad = [0.25, 0.46, 0.45, 0.94] as const;

const getDirectionStyles = (direction: MorphDirection): { readonly enter: MotionState; readonly exit: MotionState } => {
    if (direction === 'horizontal') {
        return {
            enter: { opacity: 0.6, transform: 'translateX(-100%) scale(0.95)' },
            exit: { opacity: 0.6, transform: 'translateX(100%) scale(0.95)' },
        };
    }

    return {
        enter: { opacity: 0.6, transform: 'translateY(-100%) scale(0.95)' },
        exit: { opacity: 0.6, transform: 'translateY(100%) scale(0.95)' },
    };
};

const getContainerStyles = (align: MorphAlign): CSSProperties => ({
    display: 'grid',
    overflow: 'hidden',
    ...(align === 'start' ? { minWidth: 0, width: '100%' } : {}),
});

const getContentStyles = (align: MorphAlign): CSSProperties => ({
    alignItems: 'center',
    display: 'flex',
    gridArea: '1 / 1',
    justifyContent: align === 'start' ? 'flex-start' : 'center',
    ...(align === 'start' ? { width: '100%' } : {}),
});

export const Morph: FunctionComponent<MorphProps> = ({
    align = 'center',
    children,
    direction = 'vertical',
    duration = 0.2,
    stateKey,
}) => {
    const isPresent = useIsPresent();
    const directionStyles = getDirectionStyles(direction);
    const containerStyles = getContainerStyles(align);
    const contentStyles = getContentStyles(align);

    return (
        <div style={containerStyles}>
            <AnimatePresence initial={false}>
                {isPresent ? (
                    <motion.div
                        key={stateKey}
                        animate={{
                            opacity: 1,
                            transform: 'translate(0, 0) scale(1)',
                        }}
                        exit={directionStyles.exit}
                        initial={directionStyles.enter}
                        style={contentStyles}
                        transition={{ duration, ease: easeOutQuad }}
                    >
                        {children}
                    </motion.div>
                ) : (
                    <div style={contentStyles}>{children}</div>
                )}
            </AnimatePresence>
        </div>
    );
};
